import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { FleetTenant, Overview, ToolAggregate } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorState, Scroller, Skeleton, Stat, Verdict, When, fmtInt } from '../ui/bits.js';
import { Chart } from '../ui/chart.js';
import { SitesScreen } from './sites.js';

/**
 * Overview: is anything wrong, and what happened this window.
 *
 * The front page of the console, across every site this principal can see. Not
 * to be confused with `screens/tenant/overview.ts`, which is ONE site's first
 * tab; the two answer the same question at two scopes, and their exports are
 * named apart (`OverviewScreen` here, `OverviewTab` there) so an import never
 * picks the wrong one by accident.
 *
 * WHAT IT DOES NOT REPEAT. Sites answers "is each site up" with a table, and
 * Traffic answers "what did agents do" with a window picker and a per-tool
 * breakdown. This screen is the roll-up of both and links to each: four tiles,
 * the call chart, the handful of tools doing the work, and the sites that need
 * a person. No per-site table — that is one click away and would make this the
 * Sites screen with a chart on top.
 *
 * SILENT WHEN NOTHING IS WRONG. The attention block lists only sites that are
 * degraded, broken or carrying faults, and it is absent otherwise. A block that
 * said "all good" on every visit would teach a person to skip the one place
 * where "not good" is ever printed — the same rule sites.ts keeps for faults.
 *
 * "NEVER CHECKED" SURVIVES THE ROLL-UP. On a host where the monitor is off, most
 * verdicts are `unknown`, and a Health tile reading "3 healthy" would be the
 * confident wrong number this console is built against. Unknown is counted as
 * its own thing and never folded into healthy.
 *
 * A NEW ACCOUNT NEVER SEES FOUR ZEROES. With nothing published, the front page
 * is the first-run crawl form that Sites already draws — one copy, delegated.
 *
 * The operator's failed manifests stay on `#/host`; this screen prints the same
 * one-line pointer Sites does, so a customer's first screen never carries
 * another tenant's directory name.
 */

/** How many tools the "doing the work" list names before it points at Traffic. */
const TOP_TOOLS = 5;

function healthTile(tenants: FleetTenant[]): { value: string; note: string } {
  const n = tenants.length;
  const healthy = tenants.filter((t) => t.verdict === 'healthy').length;
  const unknown = tenants.filter((t) => t.verdict === 'unknown').length;
  if (unknown === n) {
    return { value: 'Never checked', note: n === 1 ? 'no probe has run against it' : 'no probe has run against any' };
  }
  const notes = [`of ${n} ${n === 1 ? 'site is' : 'sites are'} healthy`];
  if (unknown) notes.push(`${unknown} never checked`);
  return { value: String(healthy), note: notes.join(', ') };
}

function needsAttention(t: FleetTenant): boolean {
  return t.verdict === 'degraded' || t.verdict === 'broken' || t.issues > 0;
}

function errorShare(t: ToolAggregate): string | null {
  const bad = t.byOutcome.error + t.byOutcome.rate_limited;
  if (!t.calls || !bad) return null;
  const pct = Math.round((bad / t.calls) * 100);
  return pct ? `${pct}% failed` : 'under 1% failed';
}

function siteName(o: Overview, tenantId: string | null): string {
  if (!tenantId) return '—';
  return o.tenants.find((t) => t.tenantId === tenantId)?.displayName ?? tenantId;
}

export function OverviewScreen(props: {
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { state, actions, now } = props;
  const account = state.principal?.kind === 'account';

  if (state.error) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Overview</h1>
        ${ErrorState({
          message: state.error,
          label: 'Could not load the overview.',
          body: 'Nothing below this line is a fallback: an unreachable API shows as this message and no numbers.',
          onRetry: () => actions.reload(),
        })}
      </section>
    `;
  }

  if (!state.overview) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Overview</h1>
        ${Skeleton({ rows: 4, label: 'Loading the overview…' })}
      </section>
    `;
  }

  const o = state.overview;
  if (account && o.tenants.length === 0) return SitesScreen({ state, actions, now });

  const u = o.usage;
  const faults = o.tenants.reduce((n, t) => n + t.issues, 0);
  const health = healthTile(o.tenants);
  const attention = o.tenants.filter(needsAttention);
  const busiest = [...u.byTool].filter((t) => t.calls > 0).sort((a, b) => b.calls - a.calls).slice(0, TOP_TOOLS);
  const open = account ? (state.onboard.jobs ?? []).filter((j) => !j.publishedAt && j.state !== 'cancelled' && j.state !== 'failed') : [];

  return html`
    <section class="wrap screen">
      <div class="screen__top">
        <div>
          <h1 class="screen__h" tabindex="-1" data-focus-target>Overview</h1>
          <p class="screen__meta">${u.from && u.to ? `${u.from} to ${u.to}` : 'The current window'}</p>
        </div>
      </div>

      ${!account && o.failed.length
        ? html`<p class="callout callout--warn" role="alert">
            ${o.failed.length} ${o.failed.length === 1 ? 'manifest' : 'manifests'} on disk did not
            load and ${o.failed.length === 1 ? 'is' : 'are'} not being served.${SP}
            <a href="#/host">See what failed</a>
          </p>`
        : null}

      ${open.length
        ? html`<p class="callout" role="status">
            ${open.length === 1 ? 'A crawl you started is waiting:' : `${open.length} crawls you started are waiting:`}${SP}
            ${open.map(
              (j, i) => html`${i ? ', ' : ''}<a href=${`#/crawl/${encodeURIComponent(j.id)}`}>${j.url}</a>${SP}<span class="muted"
                  >(${j.draft ? 'draft ready to publish' : j.state})</span
                >`,
            )}
          </p>`
        : null}

      <div class="stats">
        ${Stat({ label: 'Sites', value: String(o.tenants.length), note: `${fmtInt(o.tenants.reduce((n, t) => n + t.tools, 0))} tools published` })}
        ${Stat({ label: 'Calls', value: fmtInt(u.calls), note: 'tool calls in the window' })}
        ${Stat({ label: 'Health', value: health.value, note: health.note })}
        ${Stat({
          label: 'Faults',
          value: String(faults),
          note: faults ? 'parameters a binding cannot honour' : 'none found',
        })}
      </div>

      ${attention.length
        ? html`
            <h2 class="sec__h">Needs attention</h2>
            <ul class="attn">
              ${attention.map(
                (t) => html`<li class="attn__item">
                  <a class="attn__name" href=${`#/sites/${encodeURIComponent(t.tenantId)}`}>${t.displayName}</a>
                  ${Verdict({ verdict: t.verdict, checkedAt: t.lastCheckedAt })}
                  ${t.issues ? html`<span class="fault">${t.issues} fault${t.issues === 1 ? '' : 's'}</span>` : null}
                  <span class="attn__when">${When({ iso: t.lastCheckedAt, now, absent: '' })}</span>
                </li>`,
              )}
            </ul>
          `
        : null}

      <h2 class="sec__h">Calls per day</h2>
      ${Chart({ series: u.series, label: 'Tool calls per day, all sites' })}

      <h2 class="sec__h">Doing the work</h2>
      ${busiest.length
        ? Scroller({
            label: 'Busiest tools',
            children: html`<table class="fleet">
              <thead>
                <tr>
                  <th scope="col">Tool</th>
                  <th scope="col">Site</th>
                  <th scope="col" class="num">Calls</th>
                </tr>
              </thead>
              <tbody>
                ${busiest.map(
                  (t) => html`<tr class="fleet__row">
                    <th scope="row">
                      ${t.tenantId
                        ? html`<a class="fleet__open" href=${`#/sites/${encodeURIComponent(t.tenantId)}/tools`}>
                            <span class="fleet__name">${t.tool}</span>
                            ${errorShare(t) ? html`<span class="fleet__id">${errorShare(t)}</span>` : null}
                          </a>`
                        : html`<span class="fleet__name">${t.tool}</span>`}
                    </th>
                    <td>${siteName(o, t.tenantId)}</td>
                    <td class="num">${fmtInt(t.calls)}</td>
                  </tr>`,
                )}
              </tbody>
            </table>`,
          })
        : html`<p class="empty">No tool has been called in this window.</p>`}
      <p class="screen__foot"><a href="#/traffic">All traffic, by tool and outcome</a></p>
    </section>
  `;
}
